import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { useEffect } from 'react';

import type { Campaign, Targetable, Targeting } from '@feathr/blackbox';
import {
  getOmniAwarenessTargeting,
  useOmniSliceTargetables,
} from '@feathr/extender/components/Targetings';
import { useStore } from '@feathr/extender/state';

export interface IProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
  defaultPercentage?: number;
}

interface IUseOmniSliceTargetingReturn {
  onChange: (value: number) => void;
  selectedPercentage: number;
}

function useOmniSliceTargeting({
  campaign,
  targetings,
  defaultPercentage = 50,
}: Readonly<IProps>): IUseOmniSliceTargetingReturn {
  const { Targetings } = useStore();
  const { omniSliceTargetables } = useOmniSliceTargetables();

  // Find existing omni targeting
  const existingOmniSliceTargeting = getOmniAwarenessTargeting(targetings);

  // Derive selectedPercentage from the existing targeting's targetable
  const selectedPercentage = ((): number => {
    if (existingOmniSliceTargeting?.get('target_data')) {
      const targetable = omniSliceTargetables.models.find(
        (t) => t.id === existingOmniSliceTargeting.get('target_data'),
      );
      const includePercent = targetable?.get('include_percent');
      if (typeof includePercent === 'number') {
        return includePercent;
      }
    }
    return defaultPercentage;
  })();

  function getCurrentSliceTargetable(includePercent: number): Targetable | undefined {
    return omniSliceTargetables.models.find(
      (targetable) => targetable.get('include_percent') === includePercent,
    );
  }

  function createOmniTargeting(targetable: Targetable): Targeting {
    const model = Targetings.create({
      parent: campaign.get('id'),
      kind: 'omni_awareness',
      included: true,
      target_data: targetable.id,
      name: String(targetable.get('include_percent')),
    });

    runInAction(() => {
      targetings.push(model);
    });

    return model;
  }

  // Ensure omni targeting exists on initial load
  useEffect(() => {
    if (existingOmniSliceTargeting || omniSliceTargetables.isPending) {
      return;
    }

    // Create targeting with default value if it doesn't exist
    const defaultTargetable = getCurrentSliceTargetable(defaultPercentage);
    if (defaultTargetable) {
      createOmniTargeting(defaultTargetable);
    }
  }, [omniSliceTargetables.isPending]);

  function onChange(value: number): void {
    const targetable = getCurrentSliceTargetable(value);
    if (!targetable) {
      return;
    }

    // Recompute existing targeting in case it was just created
    const currentTargeting = existingOmniSliceTargeting;
    if (!currentTargeting) {
      return;
    }

    // Update existing targeting with new target_data
    runInAction(() => {
      currentTargeting.set({
        target_data: targetable.id,
        // Add the include percentage as the name to make it easier to identify
        name: String(targetable.get('include_percent')),
      });
    });
  }

  return {
    onChange,
    selectedPercentage,
  };
}

export default useOmniSliceTargeting;
